import type { Coord, GameState } from "@dailywars/core/engine";
import { useEffect, useState, type RefObject } from "react";

import { getCanvasSize } from "#/game/canvasRenderer";
import { useCoarsePointer } from "#/hooks/useCoarsePointer";

import { useBoardPinchZoom } from "./boardPinchZoomHook";
import { useBoardTouchTapHandler } from "./boardTouchTap";
import { MIN_TILE_PX_COARSE, MIN_TILE_PX_FINE, computeDisplayScale } from "./boardViewport";

function useCanvasViewportScale({
  viewportRef,
  intrinsicWidthPx,
  minTilePx,
}: {
  viewportRef: RefObject<HTMLDivElement | null>;
  intrinsicWidthPx: number;
  minTilePx: number;
}): number {
  const [displayScale, setDisplayScale] = useState(1);
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const updateScale = () => {
      const w = viewport.clientWidth;
      setDisplayScale(
        computeDisplayScale({
          containerWidth: w,
          intrinsicWidth: intrinsicWidthPx,
          minTilePx,
        }),
      );
    };
    updateScale();
    const ro = new ResizeObserver(() => {
      updateScale();
    });
    ro.observe(viewport);
    return () => ro.disconnect();
  }, [intrinsicWidthPx, minTilePx, viewportRef]);
  return displayScale;
}

export function useGameCanvasBoardDisplay({
  viewportRef,
  canvasRef,
  state,
  onTileHover,
  onTileSelect,
}: {
  viewportRef: RefObject<HTMLDivElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  state: GameState;
  onTileHover: (tile: Coord | null) => void;
  onTileSelect: (tile: Coord) => void;
}): {
  displayScale: number;
  cssWidth: number;
  cssHeight: number;
  stickInspectOnClick: boolean;
} {
  const coarsePointer = useCoarsePointer();
  const minTilePx = coarsePointer ? MIN_TILE_PX_COARSE : MIN_TILE_PX_FINE;
  const intrinsic = getCanvasSize(state);
  const baseScale = useCanvasViewportScale({
    viewportRef,
    intrinsicWidthPx: intrinsic.width,
    minTilePx,
  });
  const onBoardTouchTap = useBoardTouchTapHandler({
    canvasRef,
    boardWidth: state.width,
    boardHeight: state.height,
    onTileHover,
    onTileSelect,
  });
  const zoom = useBoardPinchZoom({
    viewportRef,
    canvasRef,
    enabled: coarsePointer,
    onTap: coarsePointer ? onBoardTouchTap : undefined,
  });
  const displayScale = baseScale * zoom;
  return {
    displayScale,
    cssWidth: intrinsic.width * displayScale,
    cssHeight: intrinsic.height * displayScale,
    stickInspectOnClick: coarsePointer,
  };
}
